'use client'

import React, { forwardRef, useState, useRef, useEffect } from 'react'
import { cn } from '../lib/utils'
import { colors } from '../tokens/colors'
import { LucideIcon, ChevronDown, Check, X } from 'lucide-react'
import { DropdownPortal, useDropdownPosition } from './DropdownPortal'

// Tipos base do multiselect
type MultiSelectSize = 'sm' | 'md' | 'lg'
type MultiSelectVariant = 'default' | 'error' | 'success'

// Interface para opções (reutilizando do Select)
export interface MultiSelectOption {
  value: string | number
  label: string
  disabled?: boolean
  icon?: LucideIcon
}

// Interface para aparência do multiselect
interface MultiSelectAppearance {
  backgroundColor?: string
  textColor?: string
  borderColor?: string
  placeholderColor?: string
}

interface MultiSelectProps {
  // Opções
  options: MultiSelectOption[]
  value?: (string | number)[]
  onChange?: (value: (string | number)[]) => void
  
  // Configuração visual
  variant?: MultiSelectVariant
  size?: MultiSelectSize
  
  // Estados
  error?: boolean
  success?: boolean
  disabled?: boolean
  
  // Ícones
  leftIcon?: LucideIcon
  
  // Labels e mensagens
  label?: string
  helperText?: string
  errorMessage?: string
  placeholder?: string
  
  // Customização
  appearance?: MultiSelectAppearance
  
  // Layout
  fullWidth?: boolean
  className?: string
  
  // Comportamento específico do MultiSelect
  searchable?: boolean
  clearable?: boolean
  required?: boolean
  maxSelections?: number
  showSelectAll?: boolean
  closeOnSelect?: boolean
}

// Presets de variantes (reutilizando do Select)
const variantPresets: Record<MultiSelectVariant, MultiSelectAppearance> = {
  default: {
    backgroundColor: colors.background.white,
    textColor: colors.text.primary,
    borderColor: colors.border.primary,
    placeholderColor: colors.text.tertiary,
  },
  error: {
    backgroundColor: colors.status.error.background,
    textColor: colors.status.error.text,
    borderColor: colors.status.error.text,
    placeholderColor: colors.text.tertiary,
  },
  success: {
    backgroundColor: colors.status.success.background,
    textColor: colors.status.success.text,
    borderColor: colors.status.success.text,
    placeholderColor: colors.text.tertiary,
  },
}

export const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(({
  options,
  value = [],
  onChange,
  variant = 'default',
  size = 'md',
  error = false,
  success = false,
  disabled = false,
  leftIcon: LeftIcon,
  label,
  helperText,
  errorMessage,
  placeholder = 'Selecione opções',
  appearance,
  fullWidth = false,
  className,
  searchable = false,
  clearable = false,
  required = false,
  maxSelections,
  showSelectAll = false,
  closeOnSelect = false,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const selectRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownPosition = useDropdownPosition(selectRef, isOpen)

  // Determinar variante baseada nos estados
  const currentVariant = error ? 'error' : success ? 'success' : variant
  
  // Determinar aparência final
  const preset = variantPresets[currentVariant]
  const finalAppearance: MultiSelectAppearance = {
    backgroundColor: appearance?.backgroundColor || (disabled ? colors.palette.gray[50] : preset.backgroundColor),
    textColor: appearance?.textColor || (disabled ? colors.palette.gray[400] : preset.textColor),
    borderColor: appearance?.borderColor || (disabled ? colors.palette.gray[200] : preset.borderColor),
    placeholderColor: appearance?.placeholderColor || preset.placeholderColor,
  }

  // Classes de tamanho
  const sizes = {
    sm: 'px-3 py-2 text-sm min-h-[2.5rem]',
    md: 'px-4 py-3 text-base min-h-[3rem]',
    lg: 'px-5 py-4 text-lg min-h-[3.5rem]',
  }

  // Encontrar opções selecionadas
  const selectedOptions = options.filter(option => value.includes(option.value))

  // Filtrar opções baseado na busca
  const filteredOptions = searchable 
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options

  // Verificar se todas as opções estão selecionadas
  const allSelected = filteredOptions.length > 0 && filteredOptions.every(option => 
    value.includes(option.value) || option.disabled
  )

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      // The dropdown lives in a portal under document.body — it must count
      // as "inside" for the outside-click check.
      if (
        selectRef.current && !selectRef.current.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus no input de busca quando abre
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  const handleSelect = (option: MultiSelectOption) => {
    if (option.disabled) return

    const isSelected = value.includes(option.value)
    let newValue: (string | number)[]

    if (isSelected) {
      // Remove da seleção
      newValue = value.filter(v => v !== option.value)
    } else {
      // Adiciona à seleção (respeitando limite máximo)
      if (maxSelections && value.length >= maxSelections) {
        return // Não adiciona se já atingiu o limite
      }
      newValue = [...value, option.value]
    }

    onChange?.(newValue)

    if (closeOnSelect && !isSelected) {
      setIsOpen(false)
      setSearchTerm('')
    }
  }

  const handleSelectAll = () => {
    const availableOptions = filteredOptions.filter(option => !option.disabled)
    
    if (allSelected) {
      // Deseleciona todas as opções filtradas
      const newValue = value.filter(v => 
        !availableOptions.some(option => option.value === v)
      )
      onChange?.(newValue)
    } else {
      // Seleciona todas as opções disponíveis (respeitando limite)
      const optionsToAdd = availableOptions.filter(option => 
        !value.includes(option.value)
      )
      
      let newValue = [...value]
      
      if (maxSelections) {
        const remainingSlots = maxSelections - value.length
        newValue = [...value, ...optionsToAdd.slice(0, remainingSlots).map(o => o.value)]
      } else {
        newValue = [...value, ...optionsToAdd.map(o => o.value)]
      }
      
      onChange?.(newValue)
    }
  }

  const handleRemoveTag = (optionValue: string | number, e: React.MouseEvent) => {
    e.stopPropagation()
    const newValue = value.filter(v => v !== optionValue)
    onChange?.(newValue)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange?.([])
  }

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  // Classes base
  const baseClasses = 'border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary cursor-pointer'
  
  // Classes do container
  const containerClasses = cn(
    'relative',
    fullWidth ? 'w-full' : 'w-auto'
  )

  // Classes do select
  const selectClasses = cn(
    baseClasses,
    sizes[size],
    LeftIcon && 'pl-10',
    'pr-10', // Sempre tem espaço para chevron
    disabled && 'cursor-not-allowed',
    className
  )

  // Estilos inline
  const selectStyles: React.CSSProperties = {
    backgroundColor: finalAppearance.backgroundColor,
    borderColor: finalAppearance.borderColor,
  }

  // Classes dos ícones
  const iconClasses = 'absolute top-4 transform -translate-y-1/2 w-5 h-5'
  const leftIconClasses = cn(iconClasses, 'left-3')
  const rightIconClasses = cn(iconClasses, 'right-3')

  // Renderizar tags selecionadas
  const renderSelectedTags = () => {
    if (selectedOptions.length === 0) {
      return (
        <span style={{ color: finalAppearance.placeholderColor }}>
          {placeholder}
        </span>
      )
    }

    return (
      <div className="flex flex-wrap gap-1">
        {selectedOptions.map((option) => (
          <span
            key={option.value}
            className="inline-flex items-center gap-1 px-2 py-1 bg-brand-primary/10 text-brand-primary rounded-md text-sm"
          >
            {option.icon && <option.icon className="w-3 h-3" />}
            <span className="truncate max-w-[8rem]">{option.label}</span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => handleRemoveTag(option.value, e)}
                className="ml-1 hover:bg-brand-primary/20 rounded-sm p-0.5 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', fullWidth && 'w-full')}>
      {/* Label */}
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium" style={{ color: finalAppearance.textColor }}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {selectedOptions.length > 0 && (
            <span className="text-xs text-gray-500">
              {selectedOptions.length} selecionado{selectedOptions.length !== 1 ? 's' : ''}
              {maxSelections && ` de ${maxSelections}`}
            </span>
          )}
        </div>
      )}

      {/* Select Container */}
      <div className={containerClasses} ref={selectRef}>
        {/* Select Input */}
        <div
          ref={ref}
          className={selectClasses}
          style={selectStyles}
          onClick={handleToggle}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-multiselectable="true"
        >
          {/* Left Icon */}
          {LeftIcon && (
            <LeftIcon 
              className={leftIconClasses}
              style={{ color: finalAppearance.placeholderColor }}
            />
          )}

          {/* Selected Tags or Placeholder */}
          <div className="flex-1 flex items-center gap-2 pr-2">
            {renderSelectedTags()}
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-1">
            {clearable && selectedOptions.length > 0 && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-4 h-4" style={{ color: finalAppearance.placeholderColor }} />
              </button>
            )}
            <ChevronDown 
              className={cn(
                'w-4 h-4 transition-transform',
                isOpen && 'rotate-180'
              )}
              style={{ color: finalAppearance.placeholderColor }}
            />
          </div>
        </div>

        {/* Dropdown — portal on document.body so no Modal/overflow clips it */}
        {isOpen && (
          <DropdownPortal
            position={dropdownPosition}
            portalRef={dropdownRef}
            className="bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden"
          >
            {/* Search Input */}
            {searchable && (
              <div className="p-2 border-b border-gray-100">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
            )}

            {/* Select All Option */}
            {showSelectAll && filteredOptions.length > 0 && (
              <div className="border-b border-gray-100">
                <div
                  className="px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 transition-colors font-medium"
                  onClick={handleSelectAll}
                >
                  <span>
                    {allSelected ? 'Desmarcar todas' : 'Selecionar todas'}
                  </span>
                  {allSelected && <Check className="w-4 h-4" />}
                </div>
              </div>
            )}

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto" role="listbox">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {searchTerm ? 'Nenhuma opção encontrada' : 'Sem opções disponíveis'}
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value)
                  const isDisabled = option.disabled || (
                    maxSelections && 
                    !isSelected && 
                    value.length >= maxSelections
                  )
                  const IconComponent = option.icon

                  return (
                    <div
                      key={option.value}
                      className={cn(
                        'px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 transition-colors',
                        isSelected && 'bg-brand-primary/10 text-brand-primary',
                        isDisabled && 'opacity-50 cursor-not-allowed'
                      )}
                      onClick={() => handleSelect(option)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <div className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="w-4 h-4" />}
                        <span className="truncate">{option.label}</span>
                      </div>
                      {isSelected && <Check className="w-4 h-4" />}
                    </div>
                  )
                })
              )}
            </div>
          </DropdownPortal>
        )}
      </div>

      {/* Helper Text / Error Message */}
      {(helperText || errorMessage) && (
        <div className="text-sm">
          {errorMessage ? (
            <span style={{ color: colors.status.error.text }}>
              {errorMessage}
            </span>
          ) : (
            <span style={{ color: colors.text.secondary }}>
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  )
})

MultiSelect.displayName = 'MultiSelect'